import React, { useRef } from "react";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";

interface ImageUploadProps {
    images: File[];
    onImagesChange: (images: File[]) => void;
    maxImages?: number;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

/**
 * Component upload ảnh giấy tờ tuỳ thân (CMND/CCCD/Passport) — hỗ trợ kéo thả, xem trước và xoá ảnh
 */
export const ImageUpload: React.FC<ImageUploadProps> = ({
    images,
    onImagesChange,
    maxImages = 3,
}) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        
        const invalidType = files.find(file => !["image/jpeg", "image/png", "image/heic", "image/webp"].includes(file.type));
        if (invalidType) {
            toast.error(`File ${invalidType.name} không đúng định dạng ảnh`);
            return;
        }

        const oversized = files.find(file => file.size > MAX_FILE_SIZE);
        if (oversized) {
            toast.error(`File ${oversized.name} quá lớn (tối đa 5MB)`);
            return;
        }

        const newImages = [...images, ...files].slice(0, maxImages);
        onImagesChange(newImages);
        if (inputRef.current) inputRef.current.value = "";
    };

    const removeImage = (index: number) => {
        onImagesChange(images.filter((_, i) => i !== index));
    };

    return (
        <div className="space-y-3">
            <div
                className="border-2 border-dashed border-border rounded-xl p-4 text-center cursor-pointer hover:border-primary hover:bg-status-success-muted/20 hover:scale-[1.01] transition-all duration-200"
                onClick={() => inputRef.current?.click()}
            >
                <Upload className="w-6 h-6 mx-auto text-gray-400 mb-1.5" />
                <p className="text-sm text-gray-600">
                    Kéo thả ảnh vào đây hoặc{" "}
                    <span className="text-primary font-medium hover:underline">
                        chọn file
                    </span>
                </p>
                <p className="text-[11px] text-gray-400 mt-0.5">JPG, PNG, HEIC, WebP</p>
            </div>
            <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/heic,image/webp"
                multiple
                className="hidden"
                onChange={handleFileChange}
            />
            {images.length > 0 && (
                <div className="flex gap-3 flex-wrap">
                    {images.map((file, index) => (
                        <div key={index} className="relative group">
                            <img
                                src={URL.createObjectURL(file)}
                                alt={`ID ${index + 1}`}
                                className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                            />
                            <button
                                onClick={() => removeImage(index)}
                                className="absolute -top-2 -right-2 size-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center"
                                type="button"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
